import eventBus from '@/services/globals/events-bus.service';

export class DataSource<T> implements IDataSource {
  sortOrder?: string;
  sortDataField?: string;
  rowsPageCount: number;
  totalRowCount: number;
  rowsPerPage: number;
  pageNumber: number;
  numberOfPages: number = 0;
  rows: Array<T> = new Array<T>();
  loading: boolean;
  filters: FiltrosModel[] = [];
  onLoad?: (rows: any) => void;

  constructor(
    pageNumber: number,
    rowsPerPage: number,
    rowsPageCount: number,
    totalRowCount: number,
    rows: T[],
  ) {
    this.pageNumber = pageNumber;
    this.rowsPerPage = rowsPerPage;
    this.rowsPageCount = rowsPageCount;
    this.totalRowCount = totalRowCount;
    this.rows = rows;
    this.calculateNumberOfPages();
    this.loading = false;
  }

  setOnLoad(onLoad: (rows: any) => void) {
    this.onLoad = onLoad;
  }

  next(): void {
    this.pageNumber++;
    this.loading = true;
    this.load()
      .then(() => this.calculateNumberOfPages())
      .finally(() => (this.loading = false));
  }
  prev(): void {
    this.pageNumber--;
    this.loading = true;
    this.load()
      .then(() => this.calculateNumberOfPages())
      .finally(() => (this.loading = false));
  }

  setSortDataField(dataField: string) {
    this.sortDataField = dataField;
  }

  load(): Promise<any> {
    this.calculateNumberOfPages();
    if (this.rows.length == 0 && this.pageNumber > 0) this.prev();
    if (this.onLoad) this.onLoad(this);

    const result = Promise.resolve(true);

    result.then(() => {
      eventBus.emit('gridLoaded');
    });

    return result;
  }

  goToPage(page: number): void {
    if (this.pageNumber == page) return;
    this.pageNumber = page;
    this.loading = true;
    this.load()
      .then(() => this.calculateNumberOfPages())
      .finally(() => (this.loading = false));
  }

  calculateNumberOfPages(): void {
    this.numberOfPages = Math.ceil(this.totalRowCount / this.rowsPerPage);
  }

  clearFilters() {
    this.filters = [];
  }
  removeFilter(datafield: string) {
    const index = this.filters.findIndex((x) => x.field == datafield);
    this.filters.splice(index, 1);
  }
  filter(rowsPerPage: number = 10) {
    this.rowsPerPage = rowsPerPage;
    this.loading = true;
    this.load()
      .then(() => this.calculateNumberOfPages())
      .finally(() => (this.loading = false));
  }
}

export class FiltrosModel {
  field: string;
  conditionBetweenFilters: string = 'and';
  filters: TypeFilters[] = new Array<TypeFilters>();
  constructor(field: string, conditionBetweenFilters: string = 'and') {
    this.field = field;
    this.filters = [];
    this.conditionBetweenFilters = conditionBetweenFilters;
  }

  addFilter(
    label: string,
    value: any,
    culture: string,
    condition: string,
    operator: string,
    field: string,
  ) {
    this.filters.push(new TypeFilters(label, value, culture, condition, operator, field));
  }

  removeFilters() {
    this.filters = [];
  }
}

export class ConsulteGridColumnsModel {
  text: string = '';
  align: string | undefined;
  cellsalign: string | undefined;
  hidden: boolean = false;
  datafield: string = '';
  editable: boolean | undefined | ((row: any) => boolean);
  filterable: boolean | undefined;
  /**
   * Se a coluna pode ser ORDENADA pelo cabeçalho.
   *
   * Existe separada de `filterable` porque as duas capacidades não andam juntas: há
   * coluna que faz sentido ordenar e não faz sentido (ou não é possível) filtrar — o
   * caso que motivou é "Desativado" em Grupos/Permissões, cujo backend não tem filtro
   * por essa coluna, então ligar `filterable` só para ganhar o ordenar abriria uma
   * caixa de filtro que não filtra nada.
   *
   * `undefined` cai em `filterable`, que era o critério único antes disto — nenhuma
   * grade existente muda de comportamento.
   */
  sortable: boolean | undefined;
  filtertype: string | undefined;
  filtertextfield: string | undefined;
  filtervaluefield: string | undefined;
  filterdata: Array<any> | undefined;
  filterlistpromise: undefined | (() => Promise<any>);
  filtermode: undefined | string;
  conditionbetweenfilters: undefined | string;
  columntype: undefined | string;
  editinputdefinition: any;
  cellsrenderer: undefined | ((index: number, dataField: string, row: any) => string);
  columngroup: undefined | string;
  /**
   * Marca esta coluna como o CABEÇALHO DE LINHA (`<th scope="row">`) da grade.
   *
   * É a coluna que IDENTIFICA o registro (o nome, a descrição). Sem ela, ao navegar
   * célula a célula o leitor de tela anuncia "coluna X: valor" mas nunca diz a QUE linha
   * aquilo pertence; o `<th scope="row">` vira a âncora que o NVDA repete como contexto ao
   * ler as demais células da mesma linha.
   *
   * `undefined`: o grid escolhe sozinho a primeira coluna visível FILTRÁVEL (heurística
   * que pula colunas de controle — botão, ícone —, que por convenção vêm com
   * `filterable: false`). Marque `true` quando quiser fixar outra coluna como âncora.
   */
  rowheader: boolean | undefined;
  freezedcolumn: boolean = false;
  /** Largura em pixels — undefined mantém o comportamento atual (auto). Preenchida quando o
   * usuário redimensiona a coluna arrastando a borda do cabeçalho; persistida por tela. */
  width: number | undefined;
}

class TypeFilters {
  constructor(
    label: string,
    value: any,
    culture: string,
    condition: string,
    operator: string,
    field: string,
  ) {
    this.label = label;
    this.value = value;
    this.culture = culture;
    this.condition = condition;
    this.operator = operator;
    this.field = field;
  }
  label: string;
  value: any;
  culture: string;
  condition: string;
  operator: string;
  field: string;
}
